import { useMemo, useReducer } from 'react';
import { useMutation } from 'react-query';
import Modal from '@components/Modal';
import Tip from '@components/Tip';
import Title from '@components/Title';
import Button from '@components/Button';
import { sceneCopy } from '@apis/tourApis';
import { List } from './List';
import { TargetList } from './TargetList';
import {
  ScenesCopyContext,
  type ScenesCopyProps,
  type ScenesCopyState,
  type ScenesCopyValue,
  defaultScenesCopyState,
} from './scenesCopyContext';
import SearchBar from './SearchBar';
import { getSceneCopyParams } from './getSceneCopyParams';

interface Props extends ScenesCopyProps {
  onComplete: () => void;
  onRequestClose: () => void;
}

export function ScenesCopyModal({
  level,
  categoryId,
  subCategoryId,
  sceneIds,
  onComplete,
  onRequestClose,
}: Props) {
  const [state, setState] = useReducer(
    (prev: ScenesCopyState, payload: Partial<ScenesCopyState>) => {
      const nextState = { ...prev, ...payload };
      // 搜索或取消搜索，清空`targetTid`
      if (payload.query !== undefined) {
        nextState.targetTid = undefined;
      }
      // 作品列表进入了新的文件夹，清空`targetTid`, 取消搜索状态
      if (payload.albumId !== undefined) {
        nextState.targetTid = undefined;
        nextState.query = '';
      }
      // // 如果点击已选中的作品，取消选中
      // if (payload.targetTid === prev.targetTid) {
      //   nextState.targetTid = undefined;
      // }

      // 如果改变了`targetCategoryId`
      if (payload.targetCategoryId !== undefined) {
        // 如果`isNewCategory`没传就设为false
        nextState.isNewCategory = !!payload.isNewCategory;
        // 如果点击已选中的分组, 取消选中
        if (
          payload.targetCategoryId === prev.targetCategoryId &&
          payload.isNewCategory === prev.isNewCategory
        ) {
          nextState.targetCategoryId = undefined;
          nextState.isNewCategory = undefined;
        }
      }

      // 如果`targetTid`改变了, 重置右侧区域状态
      if (nextState.targetTid !== prev.targetTid) {
        nextState.targetCategoryId = undefined;
        nextState.isNewCategory = undefined;
        nextState.targetTourData = undefined;
      }

      return nextState;
    },
    defaultScenesCopyState
  );

  const { mutateAsync: copy, isLoading } = useMutation(sceneCopy);

  const value: ScenesCopyValue = useMemo(
    () => ({
      ...state,
      level,
      categoryId,
      setScenesCopyState: setState,
    }),
    [state]
  );

  const getDisabled = () => {
    if (!state.targetTid) return true;
    if (level === '二级分组' || level === '场景') {
      return state.targetCategoryId === undefined;
    }
    return false;
  };

  const handleCopy = async () => {
    if (!state.targetTourData || !state.targetTid) return;
    const params = getSceneCopyParams(
      { level, categoryId, subCategoryId, sceneIds },
      {
        targetTourData: state.targetTourData,
        targetCategoryId: state.targetCategoryId ?? 0,
        isNewCategory: state.isNewCategory ?? false,
      }
    );
    await copy({ ...params, tid: state.targetTid });
    onComplete();
  };

  return (
    <ScenesCopyContext.Provider value={value}>
      <Modal
        isOpen
        style={{ width: 1000, height: 'calc(100% - 60px)' }}
        backdropZIndex={11000}
        onRequestClose={onRequestClose}
        header={
          <div className="flex w-full items-center">
            <Title>跨作品复制</Title>
            <SearchBar />
            <Tip className="ml-auto text-red">
              提醒：为避免复制出错，请确保复制到的作品没有在其他终端同时辑操作
            </Tip>
          </div>
        }
        footer={
          <>
            <Button disabled={isLoading || getDisabled()} onClick={handleCopy}>
              {isLoading ? '正在复制' : '复制到该作品'}
            </Button>
            <div className="absolute right-20 text-black-gray">
              说明：场景沙盘不支持跨作品复制
            </div>
          </>
        }
      >
        <div className="absolute inset-0 grid grid-cols-[1fr_300px]">
          <List />
          <TargetList />
        </div>
      </Modal>
    </ScenesCopyContext.Provider>
  );
}

export default ScenesCopyModal;
